import { ProfessionalProfileRepository, CreateProfessionalProfileData, UpdateProfessionalProfileData, GetProfessionalProfileData, ProfessionalProfileFilter } from '../repository/professional-profile.repository';

export class ProfessionalProfileUseCase {
  constructor(private professionalProfileRepository: ProfessionalProfileRepository) {
    console.log('[ProfessionalProfileUseCase] Constructor - Professional profile repository initialized:', !!this.professionalProfileRepository);
  }

  // ===== PROFILE MANAGEMENT =====

  async createProfessionalProfile(profileData: CreateProfessionalProfileData): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileUseCase.createProfessionalProfile] START - ProfileData:', profileData);
    try {
      // Validate profile data
      if (!profileData.userId) {
        throw new Error('User ID is required');
      }

      if (!profileData.bio || profileData.bio.length < 10) {
        throw new Error('Bio must be at least 10 characters long');
      }

      if (!profileData.specialties || profileData.specialties.length === 0) {
        throw new Error('At least one specialty is required');
      }

      if (profileData.yearsOfExperience < 0) {
        throw new Error('Years of experience cannot be negative');
      }

      if (profileData.hourlyRate && profileData.hourlyRate < 0) {
        throw new Error('Hourly rate cannot be negative');
      }

      // Check if user already has a professional profile
      const existingProfile = await this.professionalProfileRepository.getProfessionalProfileByUserId(profileData.userId);
      if (existingProfile) {
        throw new Error('User already has a professional profile');
      }

      const newProfile = await this.professionalProfileRepository.createProfessionalProfile(profileData);
      console.log('[ProfessionalProfileUseCase.createProfessionalProfile] SUCCESS - Created profile:', newProfile.id);
      return newProfile;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.createProfessionalProfile] ERROR:', error);
      throw error;
    }
  }

  async getProfessionalProfileById(profileId: string): Promise<GetProfessionalProfileData | null> {
    console.log('[ProfessionalProfileUseCase.getProfessionalProfileById] START - ProfileId:', profileId);
    try {
      if (!profileId) {
        throw new Error('Profile ID is required');
      }

      const profile = await this.professionalProfileRepository.getProfessionalProfileById(profileId);
      console.log('[ProfessionalProfileUseCase.getProfessionalProfileById] SUCCESS - Profile found:', !!profile);
      return profile;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.getProfessionalProfileById] ERROR:', error);
      throw error;
    }
  }

  async getProfessionalProfiles(filter: ProfessionalProfileFilter = {}, limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileUseCase.getProfessionalProfiles] START - Filter:', filter, 'Limit:', limitCount);
    try {
      const profiles = await this.professionalProfileRepository.getProfessionalProfiles(filter, limitCount);
      console.log('[ProfessionalProfileUseCase.getProfessionalProfiles] SUCCESS - Retrieved', profiles.length, 'profiles');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.getProfessionalProfiles] ERROR:', error);
      throw error;
    }
  }

  async getAllProfessionalProfiles(limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileUseCase.getAllProfessionalProfiles] START - Limit:', limitCount);
    try {
      const profiles = await this.professionalProfileRepository.getAllProfessionalProfiles(limitCount);
      console.log('[ProfessionalProfileUseCase.getAllProfessionalProfiles] SUCCESS - Retrieved', profiles.length, 'profiles');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.getAllProfessionalProfiles] ERROR:', error);
      throw error;
    }
  }

  async updateProfessionalProfile(profileId: string, updateData: UpdateProfessionalProfileData): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileUseCase.updateProfessionalProfile] START - ProfileId:', profileId, 'UpdateData:', updateData);
    try {
      if (!profileId) {
        throw new Error('Profile ID is required');
      }

      // Validate update data
      if (updateData.bio && updateData.bio.length < 10) {
        throw new Error('Bio must be at least 10 characters long');
      }

      if (updateData.specialties && updateData.specialties.length === 0) {
        throw new Error('At least one specialty is required');
      }

      if (updateData.yearsOfExperience !== undefined && updateData.yearsOfExperience < 0) {
        throw new Error('Years of experience cannot be negative');
      }

      if (updateData.hourlyRate !== undefined && updateData.hourlyRate < 0) {
        throw new Error('Hourly rate cannot be negative');
      }

      const updatedProfile = await this.professionalProfileRepository.updateProfessionalProfile(profileId, updateData);
      console.log('[ProfessionalProfileUseCase.updateProfessionalProfile] SUCCESS - Profile updated');
      return updatedProfile;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.updateProfessionalProfile] ERROR:', error);
      throw error;
    }
  }

  async deleteProfessionalProfile(profileId: string): Promise<void> {
    console.log('[ProfessionalProfileUseCase.deleteProfessionalProfile] START - ProfileId:', profileId);
    try {
      if (!profileId) {
        throw new Error('Profile ID is required');
      }

      await this.professionalProfileRepository.deleteProfessionalProfile(profileId);
      console.log('[ProfessionalProfileUseCase.deleteProfessionalProfile] SUCCESS - Profile deleted');
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.deleteProfessionalProfile] ERROR:', error);
      throw error;
    }
  }

  // ===== USER SPECIFIC OPERATIONS =====

  async getProfessionalProfileByUserId(userId: string): Promise<GetProfessionalProfileData | null> {
    console.log('[ProfessionalProfileUseCase.getProfessionalProfileByUserId] START - UserId:', userId);
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const profile = await this.professionalProfileRepository.getProfessionalProfileByUserId(userId);
      console.log('[ProfessionalProfileUseCase.getProfessionalProfileByUserId] SUCCESS - Profile found:', !!profile);
      return profile;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.getProfessionalProfileByUserId] ERROR:', error);
      throw error;
    }
  }

  // ===== SEARCH AND DISCOVERY =====

  async searchProfessionalsBySpecialty(specialty: string, limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileUseCase.searchProfessionalsBySpecialty] START - Specialty:', specialty, 'Limit:', limitCount);
    try {
      if (!specialty) {
        throw new Error('Specialty is required');
      }

      const profiles = await this.professionalProfileRepository.searchProfessionalsBySpecialty(specialty, limitCount);
      console.log('[ProfessionalProfileUseCase.searchProfessionalsBySpecialty] SUCCESS - Found', profiles.length, 'professionals');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.searchProfessionalsBySpecialty] ERROR:', error);
      throw error;
    }
  }

  async getAvailableProfessionals(limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileUseCase.getAvailableProfessionals] START - Limit:', limitCount);
    try {
      const profiles = await this.professionalProfileRepository.getAvailableProfessionals(limitCount);
      console.log('[ProfessionalProfileUseCase.getAvailableProfessionals] SUCCESS - Found', profiles.length, 'available professionals');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.getAvailableProfessionals] ERROR:', error);
      throw error;
    }
  }

  async getVerifiedProfessionals(limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileUseCase.getVerifiedProfessionals] START - Limit:', limitCount);
    try {
      const profiles = await this.professionalProfileRepository.getVerifiedProfessionalProfiles(limitCount);
      console.log('[ProfessionalProfileUseCase.getVerifiedProfessionals] SUCCESS - Found', profiles.length, 'verified professionals');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.getVerifiedProfessionals] ERROR:', error);
      throw error;
    }
  }

  async searchProfessionals(searchParams: {
    specialty?: string;
    location?: string;
    minExperience?: number;
    maxHourlyRate?: number;
    minHourlyRate?: number;
    language?: string;
    availableForRequests?: boolean;
  }, limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileUseCase.searchProfessionals] START - SearchParams:', searchParams, 'Limit:', limitCount);
    try {
      const filter: ProfessionalProfileFilter = {
        verificationStatus: 'verified',
        profileVisibility: 'public',
        ...(searchParams.specialty && { specialties: searchParams.specialty }),
        ...(searchParams.minExperience && { minExperience: searchParams.minExperience }),
        ...(searchParams.minHourlyRate && { minHourlyRate: searchParams.minHourlyRate }),
        ...(searchParams.maxHourlyRate && { maxHourlyRate: searchParams.maxHourlyRate }),
        ...(searchParams.language && { languagesSpoken: searchParams.language }),
        ...(searchParams.availableForRequests !== undefined && { isAvailable: searchParams.availableForRequests }),
      };

      const profiles = await this.professionalProfileRepository.getProfessionalProfiles(filter, limitCount);
      console.log('[ProfessionalProfileUseCase.searchProfessionals] SUCCESS - Found', profiles.length, 'matching professionals');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.searchProfessionals] ERROR:', error);
      throw error;
    }
  }

  // ===== VERIFICATION MANAGEMENT =====

  async submitProfileForVerification(profileId: string): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileUseCase.submitProfileForVerification] START - ProfileId:', profileId);
    try {
      if (!profileId) {
        throw new Error('Profile ID is required');
      }

      const profile = await this.professionalProfileRepository.getProfessionalProfileById(profileId);
      if (!profile) {
        throw new Error('Professional profile not found');
      }

      if (profile.verificationStatus === 'verified') {
        throw new Error('Profile is already verified');
      }

      if (profile.verificationStatus === 'pending') {
        throw new Error('Profile verification is already pending');
      }

      // Validate profile completeness before submission
      if (!profile.bio || profile.bio.length < 50) {
        throw new Error('Profile bio must be at least 50 characters long before verification');
      }

      if (profile.specialties.length === 0) {
        throw new Error('Profile must have at least one specialty before verification');
      }

      if (!profile.yearsOfExperience || profile.yearsOfExperience < 0) {
        throw new Error('Years of experience must be specified before verification');
      }

      const updatedProfile = await this.professionalProfileRepository.updateProfessionalProfile(profileId, {
        verificationStatus: 'pending'
      });

      console.log('[ProfessionalProfileUseCase.submitProfileForVerification] SUCCESS - Profile submitted for verification');
      return updatedProfile;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.submitProfileForVerification] ERROR:', error);
      throw error;
    }
  }

  async verifyProfile(profileId: string): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileUseCase.verifyProfile] START - ProfileId:', profileId);
    try {
      if (!profileId) {
        throw new Error('Profile ID is required');
      }

      const updatedProfile = await this.professionalProfileRepository.updateProfessionalProfile(profileId, {
        verificationStatus: 'verified'
      });

      console.log('[ProfessionalProfileUseCase.verifyProfile] SUCCESS - Profile verified');
      return updatedProfile;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.verifyProfile] ERROR:', error);
      throw error;
    }
  }

  async rejectProfileVerification(profileId: string, reason?: string): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileUseCase.rejectProfileVerification] START - ProfileId:', profileId, 'Reason:', reason);
    try {
      if (!profileId) {
        throw new Error('Profile ID is required');
      }

      const updatedProfile = await this.professionalProfileRepository.updateProfessionalProfile(profileId, {
        verificationStatus: 'rejected'
      });

      console.log('[ProfessionalProfileUseCase.rejectProfileVerification] SUCCESS - Profile verification rejected');
      return updatedProfile;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.rejectProfileVerification] ERROR:', error);
      throw error;
    }
  }

  async getProfilesPendingVerification(limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileUseCase.getProfilesPendingVerification] START - Limit:', limitCount);
    try {
      const profiles = await this.professionalProfileRepository.getProfilesPendingVerification(limitCount);
      console.log('[ProfessionalProfileUseCase.getProfilesPendingVerification] SUCCESS - Found', profiles.length, 'pending profiles');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.getProfilesPendingVerification] ERROR:', error);
      throw error;
    }
  }

  // ===== PROFILE STATUS MANAGEMENT =====

  async updateAvailabilityStatus(profileId: string, isAvailable: boolean): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileUseCase.updateAvailabilityStatus] START - ProfileId:', profileId, 'IsAvailable:', isAvailable);
    try {
      if (!profileId) {
        throw new Error('Profile ID is required');
      }

      const updatedProfile = await this.professionalProfileRepository.updateProfessionalProfile(profileId, {
        isAvailable
      });

      console.log('[ProfessionalProfileUseCase.updateAvailabilityStatus] SUCCESS - Availability status updated');
      return updatedProfile;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.updateAvailabilityStatus] ERROR:', error);
      throw error;
    }
  }

  async updateStudentAcceptanceStatus(profileId: string, isAcceptingStudents: boolean): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileUseCase.updateStudentAcceptanceStatus] START - ProfileId:', profileId, 'IsAcceptingStudents:', isAcceptingStudents);
    try {
      if (!profileId) {
        throw new Error('Profile ID is required');
      }

      const updatedProfile = await this.professionalProfileRepository.updateProfessionalProfile(profileId, {
        isAcceptingStudents: isAcceptingStudents
      });

      console.log('[ProfessionalProfileUseCase.updateStudentAcceptanceStatus] SUCCESS - Student acceptance status updated');
      return updatedProfile;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.updateStudentAcceptanceStatus] ERROR:', error);
      throw error;
    }
  }

  async updateProfileVisibility(profileId: string, visibility: 'public' | 'private' | 'draft'): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileUseCase.updateProfileVisibility] START - ProfileId:', profileId, 'Visibility:', visibility);
    try {
      if (!profileId) {
        throw new Error('Profile ID is required');
      }

      const updatedProfile = await this.professionalProfileRepository.updateProfessionalProfile(profileId, {
        profileVisibility: visibility
      });

      console.log('[ProfessionalProfileUseCase.updateProfileVisibility] SUCCESS - Profile visibility updated');
      return updatedProfile;
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.updateProfileVisibility] ERROR:', error);
      throw error;
    }
  }

  // ===== RATING AND REQUEST COUNT =====

  async addProfileReview(profileId: string, rating: number): Promise<void> {
    console.log('[ProfessionalProfileUseCase.addProfileReview] START - ProfileId:', profileId, 'Rating:', rating);
    try {
      if (!profileId) {
        throw new Error('Profile ID is required');
      }

      if (rating < 1 || rating > 5) {
        throw new Error('Rating must be between 1 and 5');
      }

      await this.professionalProfileRepository.updateProfileRating(profileId, rating, true);
      console.log('[ProfessionalProfileUseCase.addProfileReview] SUCCESS - Profile review added');
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.addProfileReview] ERROR:', error);
      throw error;
    }
  }

  async recordRequest(profileId: string): Promise<void> {
    console.log('[ProfessionalProfileUseCase.recordRequest] START - ProfileId:', profileId);
    try {
      if (!profileId) {
        throw new Error('Profile ID is required');
      }

      await this.professionalProfileRepository.incrementRequestCount(profileId);
      console.log('[ProfessionalProfileUseCase.recordRequest] SUCCESS - Request recorded');
    } catch (error) {
      console.error('[ProfessionalProfileUseCase.recordRequest] ERROR:', error);
      throw error;
    }
  }
}